<template>
	<div class="view-wrapper services" v-if="document">
		<h1 class="services__page-title">{{ document.title }}</h1>
		<section
			v-for="(service, index) in services"
			class="service"
			:style="{ backgroundColor: service.overview_background_color, backgroundImage: 'url(' + service.native_video_fallback_image.url + ')' }"
			:class="{ dark: (service.overview_text_color=='Dark') }"
			:id="'s' + service.slug">
			<video
			autoplay muted loop playsinline
			class="service__video"
			v-if="service.native_video_mp4.url"
			@play="handlePlay(service)"
			v-bind:poster="service.native_video_fallback_image.url">
				<source
					:src="service.native_video_mp4.url" type="video/mp4">
			</video>
			<div class="container">
				<div class="service__content" :class="{ dark: (service.overview_text_color=='Dark') }">
					<h3 class="service__title">
						<router-link
						:key="service.slug"
						:to="settings.currentLang === 'en-us' ? '/en/service/' + service.slug : '/service/' + service.slug">
							{{ service.title }}
						</router-link>
					</h3>
					<div v-html="service.abstract" />
				</div>
			</div>
			<a href="#" @click.prevent="handleAnchor(nextService(index))" class="anchor-link"></a>
		</section>
		<section class="areas">
			<div class="container">
				<div class="area" v-for="area in document.service_area_repeater">
					<div class="area__icon">
						<img :src="area.icon.sizes.bandara_medium" :alt="area.icon.alt">
					</div>
					<div class="area__content">
						<h3>{{ area.title }}</h3>
						<div v-html="area.description" />
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
import * as utils from '@/utils/utils'
import defaultHeadData from '@/utils/defaultHeadData'
import { getServiceOverviewPage } from '@/api/services.wp.api'
import smoothScroll from 'smoothscroll';

export default {
	name: 'services',
	components: {
	},
	data: () => ({
		documentData: null,
		headData: defaultHeadData
	}),
	computed: {
		settings() {
			return this.$store.state.general.settings
		},
		defaultMeta() {
			return this.$store.state.general.siteSettings[this.settings.currentLang].defaultMeta
		},
		siteSettings() {
			return this.$store.state.general.siteSettings
		},
		activeRouteUrl() {
			return utils.constructActiveRouteUrl(this.$route.fullPath)
		},
		document() {
			return this.documentData
		},
		services() {
			return this.$store.state.services.servicesList[this.settings.currentLang]
		}
	},
	watch: {
		'$route': function() {
			this.setServicePageData()
			this.updateHeadData()
		},
		defaultMeta: function() {
			this.updateHeadData()
		}
	},
	head: {
		title: function() {
			return utils.transformHeadTitle(this.headData.title)
		},
		meta: function() {
			return utils.transformHeadMeta(this.headData.meta)
		},
		link: function() {
			return utils.transformHeadLink({ data: this.headData.link, i18n: true })
		}
	},
	beforeMount() {
		this.setServicePageData()
		this.$store.dispatch('setAltLanguageLink', { lang: 'de-ch', link: '/service' })
		this.$store.dispatch('setAltLanguageLink', { lang: 'en-us', link: '/en/service' })
	},
	created() {
		window.addEventListener('scroll', this.handleScroll)
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll)
	},
	methods: {
		handleAnchor(slug) {
			if(!slug) {
				smoothScroll(document.querySelector('.areas'));
				return;
			}

			let el = document.querySelector('#s'+slug);
			smoothScroll(el);
		},
		nextService(id) {
			if(!this.services[id+1]) return false;

			return this.services[id+1].slug;
		},
		handlePlay(service) {
			service.playing = true
		},
		setServicePageData() {
			this.$store.dispatch('loadServices', this.settings.currentLang)
			this.documentData = null
			getServiceOverviewPage({ lang: this.settings.currentLang }).then(payload => {
				this.documentData = payload
				utils.setHtmlLang(this.settings.currentLang)
				this.updateHeadData()
			})
		},
		updateHeadData() {
			if (this.siteSettings[this.settings.currentLang].defaultMeta && this.documentData) {
				this.headData = utils.transformHeadData({
					meta: this.documentData.meta,
					defaultMeta: this.siteSettings[this.settings.currentLang].defaultMeta,
					settings: this.settings,
					activeRouteUrl: this.activeRouteUrl
				})
				this.$emit('updateHead')
			}
		}
	}
}
</script>

<style lang="scss">

	@import "../assets/css/helpers";

	.services {
		margin-top: -9rem;

		&__page-title {
			@include visuallyhidden;
		}

		@include mq($to: medium) {
			margin-top: 0;
		}
	}
	.service {
		height: 100vh;
		min-height: 550px;
		max-height: 1200px;
		background-color: #FE7C7C;
		margin: 0 auto;
		display: flex;
		align-items: center;
		color: #fff;
		overflow: hidden;
		position: relative;
		background-size: contain;
		background-repeat: no-repeat;
		background-position: center;

		.anchor-link {
			width: 100%;
			height: 4rem;
			position: absolute;
			bottom: 1rem;
			left: 0;
			display: block;

			&::before {
				content: "";
				display: block;
				width: 1.5rem;
				height: 1.5rem;
				border: 2px solid #fff;
				border-top-color: transparent;
				border-right-color: transparent;
				position: absolute;
				left: 50%;
				top: 0;
				transform: translateX(-50%) rotate(-45deg);
				transition: .5s transform;
			}

			&:hover::before {
				transform: translateX(-50%) translateY(10px) rotate(-45deg);
			}
		}

		&.dark {
			.anchor-link::before {
				border-bottom-color: $colorHighlight;
				border-left-color: $colorHighlight;
			}
		}

		.container {
			z-index: 1;
		}

		&__video {
			display: block;
			position: absolute;
			top: 50%;
			left: 50%;
			min-width: 100%;
			min-height: 100%;
			width: auto;
			height: auto;
			transform: translateX(-50%) translateY(-50%);
			opacity: 1;
			transition: 1s opacity;

			&.playing {
				opacity: 1;
			}
		}

		&__icon {
			width: 20%;
			flex-shrink: 0;

			img {
				width: 100%;
				height: auto;
			}
		}
		&__content {
			color: #fff;

			&.dark {
				color: $colorBrandSecondary;
			}

			p {
				font-family: $fontFamilyBase;
				font-size: 2.5rem;
				line-height: 1.5;
				max-width: 53rem;
				padding-left: 2rem;
			}
		}
		&__title {
			font-size: 1rem;
			font-weight: 600;
			letter-spacing: .1rem;
			margin-bottom: 1rem;
			text-transform: uppercase;

			a {
				color: inherit;
				text-decoration: none;
				position: relative;

				&:hover {

					&::after {
						transform: translateX(10px);
					}
					&::before {
						transform-origin: 0 0;
						transform: scale(1, 1);
					}
				}

				&::after, &::before {
					display: block;
					position: absolute;
				}
				&::after {
					content: "→";
					font-size: 1.5rem;
					right: -2rem;
					bottom: .9rem;
					width: 25px;
					height: 17px;
					pointer-events: none;
					transition: .5s transform $easeOutQuint;
				}
				&::before {
					content: "";
					left: 0;
					bottom: 0;
					width: 100%;
					height: 2px;
					background-color: #fff;
					transform: scale(0, 1);
					transform-origin: 100% 0;
					transition: .5s transform $easeOutQuint;
				}
				.dark & {
					&::before {
						background-color: $colorBrandSecondary;
					}
				}

				@include mq($to: medium) {
					&::after {
						font-size: 1rem;
						bottom: .15rem;
					}
				}
			}
		}
		&__link {
			display: inline-block;
			border: 2px solid $colorHighlight;
			text-decoration: none;
			color: $colorHighlight;
			padding: .25rem 1.5rem;
			margin-top: 1rem;
			font-size: .8rem;

			&:hover {
				background-color: $colorHighlight;
				color: #fff;
			}
		}

		@include mq($to: medium) {
			min-height: 300px;
			max-height: 600px;

			&__title {
				font-size: .9rem;
			}
			&__content {
				p {
					font-size: 1.25rem;
				}
			}
		}
	}

	.areas {
		padding: 4rem 0;
		background-color: #f8f8f8;

		.area {
			display: flex;
			align-items: center;
			max-width: 35rem;
			margin: 4rem auto;

			h3 {
				font-size: 2.5rem;
				font-weight: 600;
				color: $colorBrandMain;
				margin-bottom: .5rem;
			}
			&__icon {
				width: 5rem;
				flex-shrink: 0;

				img {
					width: 100%;
					height: auto;
				}
			}
			&__content {
				padding-left: 3rem;
			}
		}

		@include mq($to: medium) {
			padding: 1rem 0;

			.area {
				margin: 2rem 0;

				&__icon {
					width: 3rem;
				}
				&__content {
					padding-left: 1.5rem;

					p {
						font-size: .9rem;
					}
				}
				h3 {
					font-size: 1.5rem;
				}
			}
		}
	}

</style>



// WEBPACK FOOTER //
// Services.vue?c9b54b00